import { useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  countryFlag,
  localizedCountryName,
  type CountryEntry,
} from "@common/logic/countries";
import { cn } from "@/lib/utils";

/**
 * Full-screen searchable country list for the kiosk: the search box brings up
 * the on-screen keyboard and big page buttons stand in for touch scrolling.
 * Mount it only while open; closing resets the search.
 */
export function CountryListOverlay<T extends CountryEntry>({
  search: filter,
  selectedCode,
  onSelect,
  onClose,
  renderSuffix,
}: {
  search: (query: string) => T[];
  selectedCode: string | undefined;
  onSelect: (country: T) => void;
  onClose: () => void;
  renderSuffix?: (country: T) => ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const filtered = filter(search);

  const scrollByPage = (direction: 1 | -1) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollBy({
      top: direction * el.clientHeight * 0.85,
      behavior: "smooth",
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-4 mt-[4vh] flex w-full max-w-[900px] flex-col gap-4 rounded-2xl bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[28px] font-bold">{t("Select a country")}</h2>
          <button type="button" onClick={onClose} className="-m-2 p-2">
            <X className="h-8 w-8" />
          </button>
        </div>

        <div className="relative flex items-center">
          <Input
            tabIndex={-1}
            type="text"
            autoFocus
            value={search}
            onChange={(v) => setSearch(v)}
            placeholder={t("Search country")}
            disableHorizontalGrowth
          />
          {!!search && (
            <button
              type="button"
              className="absolute right-4 -m-4 p-4"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setSearch("")}
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        <div className="flex gap-3">
          <div
            ref={listRef}
            className="h-[42vh] flex-1 overflow-y-auto rounded-lg border border-gray-100"
          >
            {filtered.length === 0 ? (
              <p className="py-10 text-center text-[22px] text-gray-500">
                {t("No country found")}
              </p>
            ) : (
              filtered.map((country) => {
                const isSelected = selectedCode === country.code;
                return (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => onSelect(country)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-4 text-left text-[26px] [&+&]:border-t",
                      isSelected && "bg-bub-vivid-blue/10",
                    )}
                  >
                    <span className="text-[30px] leading-none">
                      {countryFlag(country.code)}
                    </span>
                    <span className="flex-1">
                      {localizedCountryName(country, i18n.language)}
                    </span>
                    {renderSuffix?.(country)}
                    {isSelected && (
                      <Check className="h-6 w-6 shrink-0 text-bub-vivid-blue" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="flex w-20 flex-col gap-3">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => scrollByPage(-1)}
              className="flex flex-1 items-center justify-center rounded-xl border-2 border-bub-vivid-blue/30 bg-white text-bub-vivid-blue active:bg-bub-vivid-blue/10"
            >
              <ChevronUp className="h-10 w-10" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => scrollByPage(1)}
              className="flex flex-1 items-center justify-center rounded-xl border-2 border-bub-vivid-blue/30 bg-white text-bub-vivid-blue active:bg-bub-vivid-blue/10"
            >
              <ChevronDown className="h-10 w-10" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
